import { useEffect, useRef } from 'react';
import type { PropsWithChildren, ReactElement, RefObject } from 'react';
import focusScope from 'a11y-focus-scope';
import focusStore from 'a11y-focus-store';

import { getTabbableElements } from 'common/getTabbableElements';

type ModalDialogProps = PropsWithChildren<{
  onClose: () => void;
  backdropClassName: string;
  modalClassName: string;
  scrollContainerRef: RefObject<HTMLDivElement>;
}>;

export function ModalDialog({
  onClose,
  backdropClassName,
  modalClassName,
  scrollContainerRef,
  children
}: ModalDialogProps): ReactElement {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCloseRef.current();
        return;
      }

      if (event.key === 'Tab') {
        const container = scrollContainerRef.current;
        if (!container) return;
        const tabbableElements = getTabbableElements(container);
        if (tabbableElements.length === 0) return;
        const first = tabbableElements[0];
        const last = tabbableElements[tabbableElements.length - 1];
        // a11y-focus-scope's own recapture only fires when focus lands on
        // another real element (via a focusin event). Tabbing past the
        // last focusable element clears focus to document.body instead
        // (browsers don't reliably fire focusin for that), which the
        // library's listener never sees. Handle the boundary ourselves.
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };

    const appElement = document.getElementById('a11y-application-container');
    focusStore.storeFocus();
    if (appElement) appElement.setAttribute('aria-hidden', 'true');
    focusScope.scopeFocus(scrollContainerRef.current);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      focusScope.unscopeFocus();
      if (appElement) appElement.removeAttribute('aria-hidden');
      focusStore.restoreFocus();
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [scrollContainerRef]);

  return (
    <div className={backdropClassName}>
      <div
        ref={scrollContainerRef}
        className={modalClassName}
        tabIndex={-1}
        onClick={e => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}
